package com.tovtalk.utils.extensions

import com.tovtalk.utils.nowInstant
import kotlin.time.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

/**
 * DateTime Extensions (KMP-safe: use nowInstant() instead of Clock.System)
 * Uses kotlin.time.Instant with kotlinx-datetime 0.7.x extensions directly.
 */

/**
 * Get current timestamp
 */
fun now(): Instant = nowInstant()

/**
 * Format Instant to relative time (e.g., "2 hours ago")
 */
fun Instant.toRelativeTime(): String {
    val now = now()
    val diffMs = now.toEpochMilliseconds() - this.toEpochMilliseconds()
    val diffSec = diffMs / 1000
    val diffMin = diffMs / 60_000
    val diffHours = diffMs / 3600_000
    val diffDays = diffMs / 86400_000
    return when {
        diffSec < 60 -> "Just now"
        diffMin < 60 -> "${diffMin}m ago"
        diffHours < 24 -> "${diffHours}h ago"
        diffDays < 7 -> "${diffDays}d ago"
        diffDays < 30 -> "${diffDays / 7}w ago"
        diffDays < 365 -> "${diffDays / 30}mo ago"
        else -> "${diffDays / 365}y ago"
    }
}

/**
 * Format Instant to date string
 */
fun Instant.toDateString(): String {
    val localDateTime = this.toLocalDateTime(TimeZone.currentSystemDefault())
    return "${localDateTime.day}/${localDateTime.month.ordinal + 1}/${localDateTime.year}"
}

/**
 * Format Instant to time string
 */
fun Instant.toTimeString(): String {
    val localDateTime = this.toLocalDateTime(TimeZone.currentSystemDefault())
    val hour = localDateTime.hour.toString().padStart(2, '0')
    val minute = localDateTime.minute.toString().padStart(2, '0')
    return "$hour:$minute"
}

/**
 * Check if Instant is today
 */
fun Instant.isToday(): Boolean {
    val now = now()
    val todayDate = now.toLocalDateTime(TimeZone.currentSystemDefault()).date
    val thisDate = this.toLocalDateTime(TimeZone.currentSystemDefault()).date
    return todayDate == thisDate
}

/**
 * Check if Instant is expired (for stories)
 */
fun Instant.isExpired(durationHours: Int = 24): Boolean {
    val now = now()
    val expirationMs = this.toEpochMilliseconds() + durationHours * 3600_000L
    return now.toEpochMilliseconds() > expirationMs
}
